import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { addErrorToReport, loadLanguagePack } from '@americanexpress/one-app-ducks';
import { Surface } from '@americanexpress/dls-react';
import { PageLevelError } from '@americanexpress/consumer-travel-base';
import { composeModules } from 'holocron';
import { useDispatch, useSelector } from 'react-redux';
import themeContext from '../context/themeContext';
import { MODULE_NAME } from '../constants/moduleConstants';
import SearchWidget from './SearchWidget/SearchWidget';
import { loadMembershipDomain } from '../thunks/loadMembership';
import themeOverrides from './themeOverrides.scss';
import reducer, { setSelectedTab } from '../duck';
import { HOTELS_TAB, TAB_TO_MODULE_NAME } from '../constants/linesOfBusinessConstants';
import getTabSelected from '../selectors/tabSelector';

const HomeSearch = ({
  id,
  theme,
  moduleLoadStatus,
  locale,
  languageData,
  activeTab,
  onActiveTabChange,
}) => {
  const dispatch = useDispatch();
  const selectedTab = useSelector(getTabSelected);

  useEffect(() => {
    dispatch(setSelectedTab(activeTab));
  }, [activeTab]);

  useEffect(() => {
    onActiveTabChange(selectedTab);
  }, [selectedTab]);

  if (moduleLoadStatus === 'error') {
    return (
      <IntlProvider locale={locale} messages={languageData}>
        <PageLevelError
          id="home-search-load-error"
          description={
            <FormattedMessage id="error.module_load" />
          }
        />
      </IntlProvider>
    );
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <themeContext.Provider value={theme}>
        <Surface
          id={id}
          data-module-name={MODULE_NAME}
          variant={theme === 'blue' ? 'fg-brand' : 'base'}
          className={theme === 'blue' ? themeOverrides.blueThemeWrapper : themeOverrides.whiteThemeWrapper}
        >
          <SearchWidget />
        </Surface>
      </themeContext.Provider>
    </IntlProvider>
  );
};

HomeSearch.propTypes = {
  id: PropTypes.string,
  theme: PropTypes.string,
  moduleLoadStatus: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  languageData: PropTypes.shape({}).isRequired,
  activeTab: PropTypes.string,
  onActiveTabChange: PropTypes.func,
};

HomeSearch.defaultProps = {
  id: '',
  theme: 'white',
  activeTab: HOTELS_TAB,
  onActiveTabChange: () => null,
};

export const loadModuleData = async ({
  store: { dispatch, getState },
  ownProps,
}) => {
  try {
    const state = getState();
    const selectedTab = getTabSelected(state);
    const tabUsed = ownProps?.activeTab || selectedTab;
    await Promise.allSettled([
      dispatch(loadMembershipDomain(state)),
      dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' })),
      dispatch(setSelectedTab(tabUsed)),
      dispatch(composeModules([{ name: TAB_TO_MODULE_NAME[tabUsed] }])),
    ]);
  } catch (err) {
    await dispatch(
      addErrorToReport(err, {
        collectionMethod: 'loadModuleData',
        moduleID: MODULE_NAME,
      })
    );
    throw err;
  }
};

HomeSearch.holocron = {
  name: MODULE_NAME,
  reducer,
  loadModuleData,
};

export { HomeSearch as TestableHomeSearch };
export default oneAppModuleWrapper(MODULE_NAME)(HomeSearch);
